import * as THREE from 'three'

import { COCKPIT, ENV } from '../config/constants.js'
import { CameraRig } from './CameraRig.js'
import { CarrierModel } from './CarrierModel.js'
import { F14Model } from './F14Model.js'
import { Ocean } from './Ocean.js'
import { SkyDome, sunDirectionFromEnv } from './SkyDome.js'
import { Cockpit } from '../cockpit/Cockpit.js'
import { ModelOverrides } from './ModelOverrides.js'
import { Clouds } from './Clouds.js'
import { EscortModel } from './EscortModel.js'

/**
 * Owns the renderer, the scene graph and every visual subsystem.
 *
 * Deliberately knows nothing about where the aeroplane's state came from. It is
 * handed a state object and a Carrier once per frame and does as it is told,
 * which is what made swapping a flight model for a commanded path a contained
 * change rather than a rewrite.
 */
export class SceneRoot {
  /**
   * @param {HTMLElement} container
   * @param {object} [quality] from util/device.js — pixel ratio ceiling,
   *        shadows, and how finely to tessellate the sea. Defaults to the
   *        desktop numbers, so nothing that does not pass it changes.
   */
  constructor(container, quality = {}) {
    this.quality = quality
    this.container = container

    // ---- renderer -------------------------------------------------------
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'webgl'
    container.appendChild(this.canvas)

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    })
    // A modern phone reports a device pixel ratio of 3, so drawing at "native"
    // resolution asks the GPU for nine times the pixels of a 1x display for a
    // screen you hold at arm's length. 1.5 is indistinguishable at that size
    // and four times cheaper than 3.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.maxPixelRatio ?? 2))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.05
    // Shadows are a whole extra pass over the scene from the sun's point of
    // view, into a 2048 map. On a phone that is the difference between thirty
    // frames and sixty, and what it buys is the aeroplane's shadow on a deck
    // you are usually a mile from.
    this.renderer.shadowMap.enabled = this.quality.shadows !== false
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

    // ---- scene ----------------------------------------------------------
    this.scene = new THREE.Scene()
    this.sunDir = sunDirectionFromEnv()

    this.sky = new SkyDome(this.sunDir)
    this.scene.add(this.sky.mesh)

    const haze = this.sky.hazeColor
    this.scene.fog = new THREE.Fog(haze, ENV.VISIBILITY_M * 0.28, ENV.VISIBILITY_M * 1.5)

    this.ocean = new Ocean(this.sunDir, haze, this.quality)
    this.scene.add(this.ocean.group)

    // Scattered cumulus with bases at 3,500 ft — above the CASE 1 minimum of
    // 3,000, and above the whole pattern, so it is weather you fly under
    // rather than through.
    this.clouds = new Clouds(haze, this.quality)
    this.scene.add(this.clouds.group)

    // ---- lighting -------------------------------------------------------
    this.sun = new THREE.DirectionalLight(0xfff3dd, 2.6)
    this.sun.position.copy(this.sunDir).multiplyScalar(600)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(2048, 2048)
    this.sun.shadow.camera.near = 1
    this.sun.shadow.camera.far = 1400
    const s = 340
    this.sun.shadow.camera.left = -s
    this.sun.shadow.camera.right = s
    this.sun.shadow.camera.top = s
    this.sun.shadow.camera.bottom = -s
    this.sun.shadow.bias = -0.0008
    // Pushes the comparison along the surface normal rather than along the
    // light, which is what actually kills acne on curved geometry at this
    // texel size.
    this.sun.shadow.normalBias = 0.06
    this.scene.add(this.sun)
    this.scene.add(this.sun.target)

    // Sky/sea bounce. Without it the undersides of the jet and the deck edge
    // go pure black and the whole scene reads as a night shot.
    this.hemi = new THREE.HemisphereLight(0x9fc4de, 0x14384a, 1.05)
    this.scene.add(this.hemi)

    // ---- content --------------------------------------------------------
    this.carrierModel = new CarrierModel()
    this.scene.add(this.carrierModel.group)
    // The wake is a sibling of the hull, not a child of it: water does not
    // pitch with the ship.
    this.scene.add(this.carrierModel.waterGroup)

    // The screen. Two more hulls on the water, which is what gives the sea a
    // sense of scale — an empty ocean has no features to judge distance by.
    this.escorts = new EscortModel()
    this.scene.add(this.escorts.group)

    this.aircraft = new F14Model()
    this.scene.add(this.aircraft.group)

    this.cockpit = new Cockpit()
    // The cockpit rides with the aircraft so it inherits attitude for free.
    this.aircraft.group.add(this.cockpit.group)

    this.rig = new CameraRig(container.clientWidth / Math.max(container.clientHeight, 1))

    // Optional glTF cockpit. Loaded asynchronously and non-fatally: if it
    // arrives, the procedural one is hidden; if it does not, nothing changes.
    this.models = new ModelOverrides()
    this.modelsReady = this.models.load(this.aircraft.group).then(({ cockpitLoaded, airframeLoaded, notes }) => {
      this.gltfCockpit = cockpitLoaded
      this.gltfAirframe = airframeLoaded
      if (cockpitLoaded && this.models.cockpit) {
        this.cockpit.group.visible = false
        this.models.cockpit.visible = this.rig.isCockpit
      }
      if (airframeLoaded) {
        // Hide the procedural meshes but keep the reference points alive —
        // the eye and the hook are what the whole recovery is measured from.
        this.aircraft.setProceduralVisible(false)

        // And sit in the downloaded aeroplane rather than in a stand-in. The
        // model has a real interior — canopy bow, glareshield, panel — so the
        // procedural pit's geometry comes out and only its lights stay, which
        // is the whole reason they can be hidden separately.
        this.useModelInterior = COCKPIT.useModelInterior && !cockpitLoaded
        if (this.useModelInterior) this.cockpit.setStructureVisible(false)
      }
      // Park the rest of the air wing. Done here rather than at load time
      // because it needs the airframe to have finished arriving.
      if (airframeLoaded) {
        this.models.parkOnDeck(this.carrierModel.group)
      }

      for (const n of notes) console.info(`[models] ${n}`)
      return { cockpitLoaded, airframeLoaded, notes }
    })


    this._onResize = () => this.resize()
    window.addEventListener('resize', this._onResize)
    this.resize()

    this.elapsed = 0
  }

  get camera() {
    return this.rig.camera
  }

  resize() {
    const w = this.container.clientWidth || window.innerWidth
    const h = this.container.clientHeight || window.innerHeight
    this.renderer.setSize(w, h, false)
    this.rig.setAspect(w / Math.max(h, 1))
  }

  /**
   * @param {object} ctx
   * @param {import('../rails/RailsFlight.js').RailState} ctx.state
   * @param {import('../world/Carrier.js').Carrier} ctx.carrier
   * @param {number} ctx.dt
   * @param {object} ctx.systems  configuration state for the cockpit switches
   * @param {number} ctx.ballNorm
   * @param {boolean} ctx.ballInRange
   * @param {boolean} ctx.waveoff
   */
  update(ctx) {
    const { state, carrier, dt } = ctx
    this.elapsed += dt

    // Aircraft transform straight from the FDM.
    this.aircraft.group.position.copy(state.position)
    this.aircraft.group.quaternion.copy(state.quaternion)
    this.aircraft.sync(state, dt)
    // The downloaded model carries its own gear-up and gear-down geometry, and
    // draws both unless it is told which one the aeroplane is in.
    this.models.applyState(state)

    this.carrierModel.sync(carrier)
    this.escorts.sync(carrier)

    // Ball state.
    this.carrierModel.iflols.setBall(ctx.ballNorm ?? 0, ctx.ballInRange ?? false)
    this.carrierModel.iflols.setWaveoff(!!ctx.waveoff)
    this.carrierModel.iflols.setCut(!!ctx.cut)

    // Camera before anything that depends on camera position.
    this.rig.update({ aircraft: this.aircraft, state, carrier, dt })

    this.ocean.update(this.elapsed, this.camera.position)
    this.clouds.update(this.camera.position)
    this.carrierModel.iflols.update(this.camera.position, dt)

    // Keep the shadow frustum wrapped around the jet rather than the origin.
    this.sun.target.position.copy(state.position)
    this.sun.position.copy(state.position).addScaledVector(this.sunDir, 600)

    if (this.gltfCockpit && this.models?.cockpit) {
      this.models.cockpit.visible = this.rig.isCockpit
      this.cockpit.group.visible = false
    } else if (this.useModelInterior) {
      // The downloaded model IS the cockpit. Its geometry stays on in every
      // view — including from inside, which is the point — and only the
      // procedural pit's lights ride along to make the interior readable.
      for (const light of this.cockpit.lights) light.visible = this.rig.isCockpit
    } else {
      this.cockpit.group.visible = this.rig.isCockpit
      if (this.rig.isCockpit) this.cockpit.update(ctx)
    }

    // The sky dome is locked to the camera by its shader, but its object
    // position still needs to follow so it never gets frustum-culled oddly.
    this.sky.mesh.position.copy(this.camera.position)
  }

  render() {
    this.renderer.render(this.scene, this.camera)
  }

  dispose() {
    window.removeEventListener('resize', this._onResize)
    this.ocean.dispose()
    this.clouds.dispose()
    this.escorts.dispose()
    this.sky.dispose()
    // Before the carrier disposes its own group: the parked aircraft are
    // clones living in it, and clone() SHARES geometry and materials, so a
    // traverse-and-dispose there would free the buffers the player's own
    // aeroplane is still using.
    this.models?.unparkAll()
    this.carrierModel.dispose()
    this.aircraft.dispose()
    this.cockpit.dispose()
    this.models?.dispose()
    this.renderer.dispose()
  }
}
